import { FC, useState } from "react";
import { Heading, Flex, Box, Text, Image, Icon } from "@chakra-ui/react";
import { FiPlusCircle } from "react-icons/fi";

import { SERVICES_IMAGE_URL } from "../core/constants";
import { formatDurationHM } from "../core/utils";
import EmptyService from "../assets/images/emptyService.png";

interface ServiceProps {
  service: any;
  modalDisclosure: any;
  setSelectedService: any;
  setDynamicPricing: any;
  setCart: any;
  selectedLength: any;
  selectedSize: any;
  business_id: any;
}

export const Service: FC<ServiceProps> = ({
  service,
  modalDisclosure,
  setSelectedService,
  setDynamicPricing,
  setCart,
  selectedLength,
  selectedSize,
  business_id,
}) => {
  const [pressed, setPressed] = useState<boolean>(false);

  const dynamicPrices = (() => {
    if (typeof service?.dynamic_prices === "string") {
      try {
        return JSON.parse(service?.dynamic_prices || "{}");
      } catch {
        return {} as any;
      }
    }
    return service?.dynamic_prices ?? {};
  })();

  const isDynamicPriceAvailable = Boolean(
    dynamicPrices?.length?.additional_price ||
      dynamicPrices?.size?.additional_price
  );

  const isDynamicDurationAvailable = Boolean(
    dynamicPrices?.length?.additional_duration ||
      dynamicPrices?.size?.additional_duration
  );

  const baseDurationMin = Number(service?.duration_minute || 0);
  const lengthAdditionalMin = Number(
    dynamicPrices?.length?.additional_duration ?? 0
  );
  const sizeAdditionalMin = Number(
    dynamicPrices?.size?.additional_duration ?? 0
  );

  const totalDurationMin =
    baseDurationMin + lengthAdditionalMin + sizeAdditionalMin;

  const addSelectedService = () => {
    setSelectedService(service);

    if (isDynamicPriceAvailable) {
      modalDisclosure.onOpen();

      dynamicPrices?.length?.additional_price
        ? setDynamicPricing("length")
        : setDynamicPricing("size");
    } else {
      setCart((prev: any) => [
        ...prev,
        {
          service: service,
          selectedLength,
          selectedSize,
          business_id,
          finalPrice: service.price,
          finalDuration: service.duration_minute,
        },
      ]);
    }

    setPressed(true);
  };

  return (
    <Box mt="20px" key={service._id}>
      <Flex gap="10px" mb="10px">
        <Image
          src={`${SERVICES_IMAGE_URL}/${service.serviceImage?.file_name}`}
          fallbackSrc={EmptyService}
          alt="Service image"
          objectFit={"cover"}
          w={"80px"}
          h="90px"
          borderRadius={"12px"}
        />

        <Box>
          <Heading
            fontSize={"16px"}
            color="#4F4F51"
            mb="9px"
            fontFamily={"RedHatDisplay-Bold"}
          >
            {service.name}
          </Heading>

          <Text fontSize={"14px"} color="#4F4F51">
            {service.description}
          </Text>
        </Box>
      </Flex>

      <Box>
        <Flex justify={"space-between"} align={"center"}>
          <Flex gap={"10px"}>
            <Flex align={"center"} gap={"4px"}>
              <Icon viewBox="0 0 16 16">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_700_1180)">
                    <path
                      d="M12.6666 9.33342V4.00008C12.6666 3.26675 12.0666 2.66675 11.3333 2.66675H1.99996C1.26663 2.66675 0.666626 3.26675 0.666626 4.00008V9.33342C0.666626 10.0667 1.26663 10.6667 1.99996 10.6667H11.3333C12.0666 10.6667 12.6666 10.0667 12.6666 9.33342ZM6.66663 8.66675C5.55996 8.66675 4.66663 7.77341 4.66663 6.66675C4.66663 5.56008 5.55996 4.66675 6.66663 4.66675C7.77329 4.66675 8.66663 5.56008 8.66663 6.66675C8.66663 7.77341 7.77329 8.66675 6.66663 8.66675ZM15.3333 4.66675V12.0001C15.3333 12.7334 14.7333 13.3334 14 13.3334H2.66663V12.0001H14V4.66675H15.3333Z"
                      fill="#FF8E3C"
                      fillOpacity="0.5"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_700_1180">
                      <rect width="16" height="16" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </Icon>

              <Text fontSize={"16px"} fontWeight={600} color={"#267684"}>
                £ {service.price} {isDynamicPriceAvailable ? "+" : ""}
              </Text>
            </Flex>

            <Flex align={"center"} gap={"4px"}>
              <Icon viewBox="0 0 16 16">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_700_1189)">
                    <path
                      d="M7.99337 1.33325C4.31337 1.33325 1.33337 4.31992 1.33337 7.99992C1.33337 11.6799 4.31337 14.6666 7.99337 14.6666C11.68 14.6666 14.6667 11.6799 14.6667 7.99992C14.6667 4.31992 11.68 1.33325 7.99337 1.33325ZM8.00004 13.3333C5.05337 13.3333 2.66671 10.9466 2.66671 7.99992C2.66671 5.05325 5.05337 2.66659 8.00004 2.66659C10.9467 2.66659 13.3334 5.05325 13.3334 7.99992C13.3334 10.9466 10.9467 13.3333 8.00004 13.3333Z"
                      fill="#FF8E3C"
                      fillOpacity="0.5"
                    />
                    <path
                      d="M8.33337 4.66675H7.33337V8.66675L10.8334 10.7667L11.3334 9.94675L8.33337 8.16675V4.66675Z"
                      fill="#FF8E3C"
                      fillOpacity="0.5"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_700_1189">
                      <rect width="16" height="16" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </Icon>

              <Text fontSize={"16px"} fontWeight={600} color={"#267684"}>
                {formatDurationHM(totalDurationMin)}
                {isDynamicDurationAvailable ? "+" : ""}
              </Text>
            </Flex>
          </Flex>

          <Flex
            py="8px"
            px="12px"
            borderRadius={{ base: "50%", md: "12px" }}
            bg={pressed ? "#FF8E3C" : "#FFFFFF"}
            boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
            align={"center"}
            justify={"center"}
            onClick={addSelectedService}
            gap={"6px"}
            cursor="pointer"
            w={{ base: "40px", md: "auto" }}
          >
            <Icon fontSize={"24px"} color={pressed ? "#FFFFFF" : "#FF8E3C"}>
              <FiPlusCircle />
            </Icon>

            <Text
              color={pressed ? "#FFFFFF" : "#FF8E3C"}
              fontWeight={600}
              display={{ base: "none", md: "block" }}
            >
              Book
            </Text>
          </Flex>
        </Flex>

        <Box h={"2px"} mt="10px" bg="#E9EDEF" borderRadius={"10px"} />
      </Box>
    </Box>
  );
};
